import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import SelectEntryMenu from '@databases/context-menu/select-entry-menu';
import { Position } from '@databases/context-menu/type';
import { useContextualMenu } from '@databases/contextual-menu';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
  useDatabaseStreamState,
  useRovoGradientAnimationForTableView,
} from '@databases/database-ai-first-creation-ui';
import { DragType, setPublicDragData } from '@databases/drag';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { usePresence } from '@databases/presence';
import { isModifierKeyPressed, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  addEntryToValueRange,
  selectEntryMulti,
  selectEntrySingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsEntireEntrySelected,
  selectSelectedEntireEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  IGNORED_TABLE_ELEMENT_CLASS,
  IGNORED_TABLE_ELEMENT_WIDTH_CLASS,
} from '@databases/table-resizer';
import { RIGHT_ACTIONS_MID_WIDTH } from '@databases/table-resizer/constants';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import { getIsSortingActive } from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import { captureException } from '@sentry/react';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { AI_AGENT_USER } from '@canvas/model/identity';

import { getBodyFromTable, getRowsFromBody, getTableFromBodyRow } from '../../../table';
import { isApple, shouldShowEntryPrefix } from '../../../utils';

import EntryPrefix, { type EntryIsSelectedMap } from './EntryPrefix';
import TableLayoutCellWithMenu from './TableLayoutCellWithMenu';

const SELECT_BUTTON_PADDING = 9;
const SELECT_BUTTON_WIDTH = 15;

export const DisabledTooltipContainer = styled.div`
  position: absolute;
  z-index: ${Z_INDEX.TABLE_VIEW_SELECTION_TOOLBAR};
  width: max-content;
  padding: ${token('space.200')};
  display: inline-flex;
  align-items: center;
  gap: ${token('space.100', '8px')};
  margin-top: ${token('space.negative.100', '-8px')};

  border-radius: 4px;
  background: ${token('elevation.surface', '#FFFFFF')};
  box-shadow: ${token('elevation.shadow.overlay')};
`;

type InsertRowIndicatorProps = {
  insertIndicatorPosition: Position | undefined;
};

const InsertRowIndicator = css<InsertRowIndicatorProps>`
  :after {
    content: ' ';
    position: absolute;
    left: 0;
    width: calc(100% - ${1 + RIGHT_ACTIONS_MID_WIDTH}px);
    height: 2px;
    background-color: ${colors.B200};
    opacity: 0;

    ${({ insertIndicatorPosition }) =>
      insertIndicatorPosition === Position.BEFORE
        ? // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          css`
            opacity: 1;
            top: -0.5px;
          `
        : insertIndicatorPosition === Position.AFTER &&
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          css`
            opacity: 1;
            bottom: -0.5px;
          `};
  }
`;

interface TrProps extends InsertRowIndicatorProps {
  isInitialized: boolean;
  isSortingActive: boolean;
  isVisualRefreshM1Enabled: boolean;
  isEntryPrefixHovered: boolean;
  isSelected: boolean;
  isReadOnly: boolean;
  collaboratorColor?: string;
  isStreamingRovoPresence?: boolean;
}

export const Tr = styled.tr<TrProps>`
  transition: opacity 1s;
  opacity: ${({ isInitialized }) => (isInitialized ? '1' : '0')};
  position: relative;
  --angle: 0deg;

  ${InsertRowIndicator};

  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isSortingActive }) =>
        isSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }

  ${({ isVisualRefreshM1Enabled, isEntryPrefixHovered, isSelected }) =>
    isVisualRefreshM1Enabled &&
    isEntryPrefixHovered &&
    !isSelected &&
    css`
      &:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}) {
        background-color: ${token('color.background.input.hovered', '#F8F8F8')};
      }
    `}
  td.${IGNORED_TABLE_ELEMENT_WIDTH_CLASS}:last-of-type {
    width: 9px;
  }

  ${({ isSelected, collaboratorColor, isReadOnly, isStreamingRovoPresence }) => {
    const borderColor =
      isSelected || isReadOnly
        ? undefined
        : // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
          collaboratorColor && token(collaboratorColor);

    if (isStreamingRovoPresence && !isSelected && !isReadOnly) {
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      return css`
        /* animated gradient for the entire row */
        &::before {
          content: '';
          position: absolute;
          top: ${token('space.negative.025', '-2px')};
          bottom: ${token('space.negative.025', '-2px')};
          left: calc(var(--rovograd-left, 0px) - 2px);
          right: calc(var(--rovograd-right, 0px) - 2px);
          border-radius: 3px;
          padding: ${token('space.025', '2px')};
          background: conic-gradient(
            from var(--angle, 0deg),
            ${BORDER_ORANGE} 25%,
            ${BORDER_GREEN} 25% 50%,
            ${BORDER_BLUE} 50% 75%,
            ${BORDER_PURPLE} 75% 100%
          );
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          z-index: 300;
        }
      `;
    }

    return (
      borderColor &&
      css`
        > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}) {
          border-block: 2px solid ${borderColor} !important;
        }

        > td:nth-child(2) {
          border-left: 2px solid ${borderColor} !important;
          border-radius: 3px 0 0 3px;
        }

        > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}):nth-last-child(2) {
          border-right: 2px solid ${borderColor};
          border-radius: 0 3px 3px 0;
        }
      `
    );
  }}
`;

export type DragState =
  | {
      type: 'idle';
    }
  | {
      type: 'dragging';
    }
  | {
      type: 'is-over';
      closestEdge: Edge | null;
    };

export interface TableLayoutRowProps {
  contentId: string;
  entryId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
  isDraggingActive: boolean;
  isLastInColumn: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  containerWidth: number;
  currentViewId: string;
  index: number;
  entryIsSelectedMap: EntryIsSelectedMap;
}

const TableLayoutRow = React.memo(
  React.forwardRef<HTMLElement, TableLayoutRowProps>(
    (
      {
        contentId,
        entryId,
        isContentEditable,
        canEditContent,
        canEditFields,
        editingIds,
        setEditingIds,
        isDraggingActive,
        isLastInColumn,
        onDragStart,
        containerWidth,
        currentViewId,
        index,
        entryIsSelectedMap,
      },
      ref
    ) => {
      const { view } = useView();
      const showEntryPrefix = shouldShowEntryPrefix(view);
      const viewSettings = view.settings;
      const { formatMessage } = useIntl();
      const [isHoverDragButton, hoverProps] = useHoverState();
      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const [isEntryPrefixHovered, setIsEntryPrefixHovered] = useState(false);

      const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
        'databases-frontend-visual-refresh-table-m1'
      );
      const actionBar = useSnapshot(actionBarState.state);
      const { isActionBarEnabled } = actionBar;

      const isCellSelectionEnabled = isPluginEnabled(PluginKey.CELL_SELECTION);

      const isDatabasePresenceEnabled = getExperimentValue(
        'databasePresence',
        'enabled',
        {
          fireExperimentExposure: false,
        }
      );

      const { recentCollaboratorColor, participants } = usePresence({
        grouping: { entryId: entryId },
        componentId: 'TableLayoutRow',
        isEnabled: isDatabasePresenceEnabled,
      });

      const [
        fieldIds,
        isSelected,
        selectedRows,
        isSortingActive,
        isFirstEntry,
        isLastEntry,
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseDraftByContentId(state, {
          contentId,
          viewSettings,
          includeDeleted: true,
        });
        const isSelected = selectIsEntireEntrySelected(state, {
          entryId,
        });
        const isSortingActive = getIsSortingActive(viewSettings);

        return [
          database.fields,
          isSelected,
          selectSelectedEntireEntryIds(state, {
            contentId,
          }),
          isSortingActive,
          database.entries.indexOf(entryId) === 0,
          database.entries.indexOf(entryId) === database.entries.length - 1,
        ] as const;
      });

      const [isInitialized, setIsInitialized] = useState(true);
      useEffect(() => {
        setIsInitialized(true);
      }, []);

      const dispatch = defaultStoreUtils.useDispatch();
      const handleSelectRow = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn =
            !!e && isModifierKeyPressed(e, [isApple ? 'metaKey' : 'ctrlKey', 'shiftKey']);
          const isRangeSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
          // Blur the active input element when the cell selection mode has been 'cell' before
          blurActiveElement();
          removeTextSelection();
          if (isRangeSelectOn) {
            dispatch(
              selectEntryMulti({
                contentId,
                entryId,
                isRangeSelect: !isMultiSelectOn,
              })
            );
          } else {
            dispatch(
              selectEntrySingle({
                contentId,
                entryId,
              })
            );
          }
          dispatch(setSoftSelection({ value: false }));
        },
        [contentId, dispatch, entryId]
      );

      const handleSelectCells = useCallback(
        () =>
          dispatch(
            addEntryToValueRange({
              contentId,
              entryId,
            })
          ),
        [dispatch, contentId, entryId]
      );

      const { closeMenu } = useContextualMenu();

      const dragImage = useRef<HTMLElement>();
      const trRef = useRef<HTMLTableRowElement>(null);
      const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isRovoAParticipant = participants.some(
        (p) => p.identity.accountId === AI_AGENT_USER.accountId
      );
      const { isStreaming } = useDatabaseStreamState();
      const { entryIdSelectedForProcessing } = useSnapshot(aiFirstCreationStreamingState);
      const isStreamingRovoPresence =
        isStreaming &&
        isDbAiFirstCreationEnabled &&
        isRovoAParticipant &&
        entryId === entryIdSelectedForProcessing;
      const dragImageRootRef = useRef<Root>();

      // Animate rovo gradient border and compute insets
      useRovoGradientAnimationForTableView(
        trRef as React.RefObject<HTMLElement>,
        isStreamingRovoPresence
      );

      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }
          removeTextSelection();
          closeMenu();

          const tr = e.target as HTMLTableRowElement;
          const table = getTableFromBodyRow(tr);
          const tbody = getBodyFromTable(table);
          const trs = getRowsFromBody(tbody);
          const selectedTrs = trs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );
          const currentIndexInSelectedTrs = selectedTrs.indexOf(trRef!.current!);
          const selectedTrsIndexOffsetHeight = selectedTrs
            .slice(0, currentIndexInSelectedTrs + 1)
            .reduce((p, c) => p + c.clientHeight, 0);

          const tBody = document.createElement('tbody');
          // clone the table element to get the table styles (but do not include descendants)
          const tableClone = table?.cloneNode(false) as HTMLTableElement;
          const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement;
          const clonedTrs = getRowsFromBody(tbodyClone);
          const clonedSelectedTrs = clonedTrs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          ) as HTMLElement[];

          const originalWidths = Array.from(tr.cells).map((cell) => cell.offsetWidth);
          // Ignore widths of row number and selector cells when !isVisualRefreshM1Enabled

          const dragImageWidth = !isVisualRefreshM1Enabled
            ? originalWidths.slice(2).reduce((sum, num) => sum + num, 0) -
              SELECT_BUTTON_PADDING +
              1
            : originalWidths.reduce((sum, num) => sum + num, 0);
          const dragImageWidthForScrollableFields = !isVisualRefreshM1Enabled
            ? containerWidth - 10 - SELECT_BUTTON_PADDING - SELECT_BUTTON_WIDTH
            : containerWidth;

          const ignoredTrs = clonedSelectedTrs.flatMap(
            (el: HTMLElement) =>
              Array.from(
                el.querySelectorAll(`.${IGNORED_TABLE_ELEMENT_CLASS}`)
              ) as HTMLElement[]
          );

          ignoredTrs.forEach((ignoredTr: HTMLElement) => {
            ignoredTr.style.width = '0';
          });

          if (!isVisualRefreshM1Enabled) {
            const ignoredEntryPrefixes = clonedSelectedTrs.flatMap(
              (el: HTMLElement) =>
                Array.from(
                  el.querySelectorAll('.entry-number-table-data')
                ) as HTMLElement[]
            );

            ignoredEntryPrefixes.forEach((entryPrefix: HTMLElement) => {
              entryPrefix.style.opacity = '0';
            });

            // resize table to just display values, not row number or space for selector
            clonedSelectedTrs.forEach((el) => {
              const valueOnlyGrid = [0, 0, ...originalWidths.slice(2)];

              const valueOnlyGridTemplateColumns = valueOnlyGrid.map(
                (width) => `${width}px`
              );
              el.style.gridTemplateColumns = valueOnlyGridTemplateColumns.join(' ');
            });
          } else {
            clonedSelectedTrs.forEach((el: HTMLElement) => {
              const valueOnlyGridTemplateColumns = originalWidths.map(
                (width) => `${width}px`
              );
              el.style.gridTemplateColumns = valueOnlyGridTemplateColumns.join(' ');
            });
          }

          // select handle
          const selectButton = clonedSelectedTrs
            .find((el) => el.draggable)
            ?.querySelector(`.${TABLE_CLASSNAMES.SELECT_BUTTON}`) as HTMLElement | null;
          if (selectButton && !isVisualRefreshM1Enabled) {
            selectButton.style.left = '-9px';
            selectButton.classList.add(TABLE_CLASSNAMES.SHOW_AS_DRAGGING);
          }
          dragImage.current = document.createElement('div');
          table?.parentElement?.append(dragImage.current);
          if (isVisualRefreshM1Enabled) {
            const tableCloneEntryPrefix = clonedSelectedTrs[0]?.querySelector(
              '.entry-number-table-border'
            ) as HTMLElement;
            if (!tableCloneEntryPrefix) {
              captureException(
                new Error('Missing tableCloneEntryPrefix during drag operation'),
                {
                  level: 'warning',
                  tags: {
                    component: 'TableLayoutRow',
                    operation: 'handleDragStart',
                    issue: 'missing_entry_prefix_border',
                    captureLocation: 'table-layout-row-handle-drag-start',
                  },
                  extra: {
                    selectedRows,
                    selectedTrs,
                    clonedTrs,
                    clonedSelectedTrs,
                    selectedRowsCount: selectedRows.length,
                    selectedTrsCount: selectedTrs.length,
                    clonedTrsCount: clonedTrs.length,
                    clonedSelectedTrsCount: clonedSelectedTrs.length,
                    selectButton,
                    tableClone,
                    event: {
                      type: e.type,
                      target: e.target,
                      currentTarget: e.currentTarget,
                    },
                  },
                }
              );
            }
            tableCloneEntryPrefix.style.marginTop = '0px';
            tableClone.style.backgroundColor = 'transparent';
          }
          tableClone.append(tBody);
          // eslint-disable-next-line canvas/no-spread-arguments
          tBody.append(...clonedSelectedTrs);
          dragImage.current.append(tableClone);
          if (!isSortingActive) {
            Object.assign(dragImage.current.style, {
              // dragImageWidth will be used when table does not warrant field scrolling
              width: `${Math.min(dragImageWidth, dragImageWidthForScrollableFields)}px`,
              overflow: 'hidden',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
              paddingLeft: `${SELECT_BUTTON_PADDING}px`,
            });
            e.dataTransfer.setDragImage(
              dragImage.current,
              SELECT_BUTTON_WIDTH / 2,
              selectedTrsIndexOffsetHeight - tr?.clientHeight / 2
            );
          } else {
            // Use a error message as the drag image when sorting is active
            dragImageRootRef.current = createRoot(dragImage.current);
            Object.assign(dragImage.current.style, {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
            });
            dragImageRootRef.current.render(
              <DisabledTooltipContainer isEntryCardActionBar={false}>
                <CrossCircleIcon label="" color={token('color.icon.danger', '#C9372C')} />
                <div>
                  {formatMessage(
                    messages['table-layout.reset-sorting-to-enable-row-dragging']
                  )}
                </div>
              </DisabledTooltipContainer>
            );
            e.dataTransfer.setDragImage(dragImage.current, 0, 25);
          }

          setPublicDragData(e.dataTransfer, {
            type: DragType.ENTRY_ROWS,
            contentId,
            draggingIds: selectedRows,
            dragOverId: entryId,
            height: trRef.current!.offsetHeight,
            width: trRef.current!.offsetWidth,
          });

          onDragStart?.(e, entryId);
        },
        [
          closeMenu,
          containerWidth,
          contentId,
          entryId,
          formatMessage,
          isSortingActive,
          isVisualRefreshM1Enabled,
          onDragStart,
          selectedRows,
        ]
      );

      const handleDragEnd = useCallback((e: React.DragEvent) => {
        if (e.target !== e.currentTarget) {
          // Ignore dragging descendent elements
          return;
        }

        if (dragImage.current) {
          dragImage.current.remove();
          dragImage.current = undefined;
        }

        if (dragImageRootRef.current) {
          dragImageRootRef.current.unmount();
          dragImageRootRef.current = undefined;
        }
      }, []);

      const showRowAsDisabled = isSelected && isDraggingActive;

      const [isHoverAddRowBeforeButton, addRowBeforeButtonHoverProps] = useHoverState();
      const [isHoverAddRowAfterButton, addRowAfterButtonHoverProps] = useHoverState();

      // Pragmatic drag and drop
      useEffect(() => {
        if (!trRef.current) {
          return;
        }
        const element = trRef.current;
        const dragHandle =
          element.querySelector('[data-testid="row-select-button"]') || undefined;

        return combine(
          draggable({
            element,
            dragHandle,
            getInitialData() {
              return { type: DragType.ENTRY_ROWS, index, entryId };
            },
            onDragStart() {
              setDragState({ type: 'dragging' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          }),
          dropTargetForElements({
            element,
            canDrop({ source }) {
              return (
                (source.data.type === DragType.ENTRY_ROWS &&
                  source.data.entryId !== entryId) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            getData({ input, element }) {
              const data = { entryId, index };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['top', 'bottom'],
              });
            },
            onDragEnter(args) {
              if (args.source.data.type === DragType.ENTRY_ROWS) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data),
                });
              } else if (args.source.data.type === DragType.FILL_VALUES) {
                const destination = args.location.current.dropTargets[0];

                if (destination) {
                  handleSelectCells();
                }
              }
            },
            onDrag(args) {
              if (args.source.data.type === DragType.ENTRY_ROWS) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge,
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          })
        );
      }, [index, ref, entryId, handleSelectCells]);

      return (
        <Tr
          ref={composeRefs(ref, trRef)}
          className={classNames({
            [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
            [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showRowAsDisabled,
          })}
          collaboratorColor={recentCollaboratorColor}
          draggable={canEditContent && isHoverDragButton}
          insertIndicatorPosition={
            isHoverAddRowBeforeButton
              ? Position.BEFORE
              : isHoverAddRowAfterButton
              ? Position.AFTER
              : undefined
          }
          isEntryPrefixHovered={isEntryPrefixHovered}
          isSortingActive={isSortingActive}
          isInitialized={isInitialized}
          isSelected={isSelected}
          isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          isReadOnly={!canEditContent}
          isStreamingRovoPresence={isStreamingRovoPresence}
        >
          {canEditContent && showEntryPrefix && (
            <SelectEntryMenu
              contentId={contentId}
              currentViewId={currentViewId}
              canEditContent={canEditContent}
              entryId={entryId}
              hoverProps={hoverProps}
              handleSelectEntry={handleSelectRow}
              isActionBarEnabled={isActionBarEnabled}
            />
          )}
          <EntryPrefix
            contentId={contentId}
            entryId={entryId}
            entryIsSelectedMap={entryIsSelectedMap}
            index={index}
            isFirstEntry={isFirstEntry}
            isLastEntry={isLastEntry}
            isSelected={isSelected}
            setEntryHover={setIsEntryPrefixHovered}
            isReadOnly={!canEditContent}
          />
          {fieldIds.map((fieldId) => (
            <TableLayoutCellWithMenu
              key={fieldId}
              contentId={contentId}
              entryId={entryId}
              fieldId={fieldId}
              isContentEditable={isContentEditable}
              canEditContent={canEditContent}
              canEditFields={canEditFields}
              editingIds={editingIds}
              setEditingIds={setEditingIds}
              isFirstInRow={fieldIds[0] === fieldId}
              isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
              isLastInColumn={isLastInColumn}
              isSelectionDisabled={isDraggingActive || !isCellSelectionEnabled}
              isInsertContentDisabled={isDraggingActive}
              showAsDisabled={showRowAsDisabled}
              addRowBeforeButtonHoverProps={addRowBeforeButtonHoverProps}
              addRowAfterButtonHoverProps={addRowAfterButtonHoverProps}
              currentViewId={currentViewId}
              dropIndicatorEdge={
                dragState.type === 'is-over' ? dragState.closestEdge : null
              }
              currentSortFieldId={viewSettings.sort?.fieldId}
            />
          ))}
          {
            <td
              className={classNames([TABLE_CLASSNAMES.ACTIONS], {
                [IGNORED_TABLE_ELEMENT_CLASS]: true,
                ...(isVisualRefreshM1Enabled && {
                  [IGNORED_TABLE_ELEMENT_WIDTH_CLASS]: true,
                }),
              })}
            />
          }
        </Tr>
      );
    }
  )
);

export default TableLayoutRow;
